<script setup lang="ts">
import { addMonths } from 'date-fns';
import { computed, ref, toRef } from 'vue';

import type { ITrackerDealForm, ITrackerDealFormComposableOptions } from '@modules/affiliates-approved/submodules/trackers-settings/composables';
import type { VFormRef } from '@modules/forms/components/VForm/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VDialog from '@components/dialogs/VDialog/VDialog.vue';
import {

  useTrackerDealForm,
} from '@modules/affiliates-approved/submodules/trackers-settings/composables';
import { useFormatMonthDate } from '@modules/core/composables/datetime';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { useI18n } from '@modules/i18n/composables';
import { utcDateToLocal } from '@utils/date';

import type { IBulkReplacementDealsEmits, IBulkReplacementDealsProps } from './types';
import { TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS } from '../../constants';
import { useChangeTrackerDealsMutation } from '../../mutations';
import { useAffiliateTrackerConditionsQuery } from '../../queries';

const {
  selectedIds = [],
  filterId = 0,
  page = 1,
  affId,
} = defineProps<IBulkReplacementDealsProps>();
const emit = defineEmits<IBulkReplacementDealsEmits>();

const visible = defineModel<boolean>('visible');

const { formatMonthDate } = useFormatMonthDate();
const { getTranslation } = useI18n();

const isQueryEnabled = ref(false);
const form = ref<VFormRef>();

const { data, isLoading } = useAffiliateTrackerConditionsQuery(toRef(() => affId), isQueryEnabled);

const minDate = computed(() => (data.value?.last_closed_month ? addMonths(utcDateToLocal(data.value?.last_closed_month), 1) : undefined));

const {
  mutateAsync,
  isLoading: isChangingLoading,
  errors,
  clearError,
} = useChangeTrackerDealsMutation();

const formOptions = computed<ITrackerDealFormComposableOptions>(() => ({
  conditions: data.value?.conditions ?? [],
  isLoading: isLoading.value,
  minDate: minDate.value,
  conditionLabel: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'SETTINGS_TRACKERS_FORM_CONDITION').value,
  dateLabel: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'SETTINGS_TRACKERS_FORM_DATE').value,
}));

const { formSchema } = useTrackerDealForm(formOptions);

function onFormSubmit(): void {
  form.value?.submit();
}

async function onSubmit({ condition_id, date }: ITrackerDealForm): Promise<void> {
  await mutateAsync({
    id: affId,
    condition_id,
    date: formatMonthDate(date),
    segment_id: filterId,
    selected_tracker_ids: selectedIds,
    page,
  });

  close();
}

function onShow(): void {
  isQueryEnabled.value = true;
}

function onHide(): void {
  isQueryEnabled.value = false;
  emit('hide');
}

function close(): void {
  visible.value = false;
}
</script>

<template>
  <VDialog
    v-model:visible="visible"
    :title="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'BULK_REPLACEMENT_DEALS_TITLE')"
    @show="onShow"
    @hide="onHide"
  >
    <VForm
      ref="form"
      :schema="formSchema"
      :is-loading="isChangingLoading"
      :custom-errors="errors"
      @submit="onSubmit"
      @interacted="clearError"
    />
    <template #footer>
      <VButton
        :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'BULK_REPLACEMENT_DEALS_CANCEL')"
        outlined
        @click="close"
      />
      <VButton
        :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'BULK_REPLACEMENT_DEALS_SAVE')"
        :loading="isChangingLoading"
        @click="onFormSubmit"
      />
    </template>
  </VDialog>
</template>
